import { buildInfo, shortCommit } from '@brush/build-info';
import { playerWebUrl } from '../authConfig';

/**
 * The staff door. Direction C — "The Rail", variant B, "Red Field".
 *
 * The brand block scaled up until the panel *is* the parallelogram: half the composition in signal
 * orange with the lockup on it, and one column beside it that does exactly one thing.
 *
 * <b>Two deliberate departures from the mockup, both about orange.</b> This is the gallery side,
 * where `rail.css` reserves the accent for work waiting on somebody and says so in as many words —
 * `--color-felt-700`, the primary button, is charcoal here precisely so that nothing merely
 * clickable wears it. So:
 *   - <b>The call to action is chalk, not the mockup's orange.</b> Orange would have made the one
 *     control on the page the same colour as the largest thing on it that cannot be pressed, which
 *     is this direction's own description of a bug. It is not `bg-felt-700` either: that button is
 *     charcoal because it sits on a chalk ground, and inverting the ground takes its contrast with
 *     it (#3a3f46 on #1f2227 is 1.6:1). Chalk-on-charcoal is the same idea the right way up.
 *   - <b>The orange field itself is the brand mark, not a signal.</b> `RoomShell` already sets the
 *     lockup on `bg-signal`; this is that block at the size of a door. Worth knowing if the accent
 *     rule is ever audited — the exception is the mark, and it stops at the mark.
 *
 * <b>It also inverts room-web's palette</b>, via `.onair` — the class is named for player-web but
 * what it *is* is the dark half of the same token set, so the door is built from real tokens
 * rather than hexes. That is the one thing here that disagrees with the direction as written, and
 * the consequence is a dark door opening onto a chalk app. Dropping `.onair` puts it back on the
 * gallery's own ground, and the button then wants to be `bg-felt-700` again.
 *
 * Presentational, and fetches nothing: there is nothing a staff door could ask for before a token.
 */
export function LandingScreen({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="onair flex min-h-dvh flex-col bg-ground text-ink-900">
      {/*
        <b>The halves fill the window; only their contents are capped.</b> Capping the whole
        composition the way player-web's door does is wrong here, and visibly so: there the left
        half is a mark on the same ground as everything else, so the cap is invisible, while here it
        has a colour of its own — so a capped panel stops being a field bleeding off the edge and
        becomes an orange stripe floating in dark ground with a margin either side. The field runs
        to the viewport edge; the lockup and the sign-in column are held at the 1440 drawing's own
        distance from the seam by a 720px box inside each half, anchored to the middle.
      */}
      <div className="flex flex-1 flex-col lg:flex-row">
        {/*
          <b>The slant turns with the layout.</b> From `lg` the field is the left half and its right
          edge leans: that edge is the seam between the halves, so a diagonal there is the brand's
          parallelogram. Below `lg` the field spans the screen and has no seam — the same cut on its
          right edge, at any size, reads as a corner sliced off (it was tried at 32px and still did).
          So on a phone the *bottom* edge carries it instead, rising to the right: the desktop's
          shape turned through ninety degrees with the stack, the way room-web's section panel turns
          the nav's active rail. Two whole `clip-path`s rather than one polygon over a variable,
          because the point that moves is a different vertex, not the same one moved further.

          <b>Below `lg` it is one centred stack, on the pattern of a phone sign-in</b>: the field is
          the artwork across the top two-fifths with the lockup centred on it and the spade behind, then
          one short headline, one action, and the fine print at the foot. The first phone layout
          stacked the desktop's halves — a 175px strip, then a column centred in everything left —
          which opened a 200px hole between them; the second closed it but kept the desktop's
          left-aligned column at nearly desktop size, which still read as a squeezed desktop.
        */}
        <div
          className="relative flex min-h-[44dvh] overflow-hidden bg-signal short:min-h-[34dvh] [clip-path:polygon(0_0,100%_0,100%_calc(100%_-_40px),0_100%)] lg:min-h-0 lg:w-1/2 lg:[clip-path:polygon(0_0,100%_0,calc(100%_-_96px)_100%,0_100%)]"
        >
          {/*
            6% rather than the mockup's 11%. That value was drawn against a panel 900px tall, where
            the mark is anchored below the bottom edge and only its shoulders show; a taller window
            gives the same mark more field to occupy and it stops being texture behind the lockup
            and becomes a large dark shape under it.
          */}
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[240px] w-[240px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.06] short:h-[150px] short:w-[150px] lg:-bottom-[110px] lg:left-auto lg:right-10 lg:top-auto lg:h-[620px] lg:w-[620px] lg:translate-x-0 lg:translate-y-0"
            aria-hidden="true"
          >
            <path
              d="M12 2C12 2 3.6 8.7 3.6 13.8a4.3 4.3 0 0 0 7.4 3c-.2 2.5-1 4.2-2.3 5.2h6.6c-1.3-1-2.1-2.7-2.3-5.2a4.3 4.3 0 0 0 7.4-3C20.4 8.7 12 2 12 2z"
              fill="#000000"
            />
          </svg>

          <div className="relative mx-auto flex w-full max-w-[468px] flex-col items-center justify-center px-6 py-10 text-center short:py-5 lg:ml-auto lg:items-stretch lg:text-left lg:mr-0 lg:h-full lg:max-w-[720px] lg:justify-center lg:py-0 lg:pl-[84px] lg:pr-[120px]">
            <span className="block text-[44px] font-bold italic leading-[0.98] tracking-[0.01em] text-white short:text-[30px] lg:text-[86px]">
              BRUSH
            </span>
            {/*
              The word that makes this door the staff one. Tracking rather than weight, so it reads
              as a qualifier on the mark rather than as a second mark. Tracking also trails the last
              letter, so centred it sits a letter-space left of true — the matching `pl` puts it back.
            */}
            <span className="mt-2 block pl-[0.34em] text-[16px] font-normal leading-none tracking-[0.34em] text-white/[0.82] short:text-[13px] lg:mt-2.5 lg:pl-0 lg:text-[30px]">
              ROOM
            </span>
          </div>
        </div>

        <div className="relative mx-auto flex w-full max-w-[720px] flex-1 flex-col px-6 pb-6 pt-12 short:pb-3 short:pt-5 lg:mr-auto lg:ml-0 lg:min-w-0 lg:flex-row lg:items-center lg:py-0 lg:pl-6 lg:pr-[92px]">
          <div className="mx-auto flex w-full max-w-[400px] flex-col text-center lg:mx-0 lg:max-w-[420px] lg:text-left">
            <h1 className="text-[30px] font-semibold uppercase leading-[1.05] tracking-[0.005em] short:text-[22px] lg:text-[60px] lg:leading-[1.02]">
              Run the room.
            </h1>

            <p className="mt-3 text-[15px] leading-[1.5] text-ink-800 short:mt-2 short:text-[13px] lg:mt-5 lg:text-[16px]">
              Staff access for rooms running Brush.
            </p>

            {/*
              Desktop only. On a phone it says exactly what the button beneath it says, and the
              stack is short enough that a heading over a single control is furniture.
            */}
            <h2 className="mt-11 hidden text-[24px] font-semibold uppercase leading-[1.1] tracking-[0.02em] lg:block">
              Staff sign-in
            </h2>

            <button
              type="button"
              onClick={onSignIn}
              className="mt-8 flex min-h-[48px] w-full items-center justify-center bg-ink-900 text-[15px] font-semibold uppercase tracking-[0.09em] text-ground transition hover:opacity-90 short:mt-5 lg:mt-5 lg:min-h-[54px] lg:text-[16px]"
            >
              Sign in
            </button>

            {/*
              The routing line, and the reason it has two forms. A player who lands here is in the
              wrong app and the useful thing is the address of the right one — but that address is
              per environment (`stg.brushpoker.com` against production's own), so a literal in the
              bundle would send staging's staff to a host that is not this deployment's. Unset, the
              sentence still does its job without naming somewhere that may not answer; the dead
              `href="#"` the mockup carried is the one shape it must not take.
            */}
            <p className="mt-[18px] text-[13px] leading-[1.6] text-ink-800 short:mt-3">
              {playerWebUrl ? (
                <>
                  Looking for your player card? That&apos;s over at{' '}
                  <a
                    href={playerWebUrl}
                    className="text-signal underline-offset-2 transition hover:underline"
                  >
                    {hostOf(playerWebUrl)}
                  </a>
                  .
                </>
              ) : (
                <>Looking for your player card? This is the staff app, not the player portal.</>
              )}
            </p>
          </div>

          {/*
            A plain span rather than `VersionBadge`, and nothing opens behind it: the About dialog
            reports the API's version alongside this one, and the client that can ask for it does
            not exist before sign-in. Half the answer is worth having here — known gap 8's failure
            mode is a clean clone with no `.env` looking exactly like a broken app *on this screen*.

            Pinned to the capped column rather than to the window, unlike the halves behind it. A
            build stamp is read beside the thing it describes; against the right edge of an
            ultrawide it is a line on its own in the corner of an empty field. Below `lg` it is not
            absolute at all: it is the last thing in the stack, centred at the foot of the screen.
          */}
          <span className="num mt-auto self-center pt-8 text-[11px] tracking-[0.06em] text-ink-800 short:pt-4 lg:absolute lg:bottom-5 lg:right-7 lg:mt-0 lg:self-auto lg:pt-0">
            v{buildInfo.version}
            {buildInfo.commit ? <span> · {shortCommit(buildInfo.commit)}</span> : null}
          </span>
        </div>
      </div>
    </div>
  );
}

/** The host alone reads as an address a person could type; the scheme and any path do not. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
